/**
 * MultiBuy Builder - multibuy.js
 * Bootstraps every MultiBuy section on the page: curated slider, curated
 * card actions (View Collection / Share), the "Create Your Own Pack" grid
 * with its search/filter/sort toolbar, the sticky footer, the bottom
 * sheet, and the quick view drawer. Loaded last, after every other module.
 */
(function (window, document) {
  'use strict';

  var MB = (window.MB = window.MB || {});
  var utils = MB.utils;
  var store = MB.store;

  function initSection(sectionEl) {
    var sectionId = sectionEl.dataset.mbSectionId;

    var rules = utils.parseJSONScript('mb-rules-' + sectionId) || [];

    initCuratedBundles(sectionEl, sectionId, rules);
    initCreateYourOwnPack(sectionEl, sectionId, rules);
  }

  // ---------------------------------------------------------------------
  // Curated bundle cards + slider
  // ---------------------------------------------------------------------

  function initCuratedBundles(sectionEl, sectionId, rules) {
    var slider = utils.qs('[data-mb-slider]', sectionEl);
    if (slider) initSlider(slider);

    utils.qsa('[data-mb-curated-card]', sectionEl).forEach(function (card) {
      var bundleId = card.dataset.bundleId;
      var config = utils.parseJSONScript('mb-bundle-config-' + bundleId) || {};
      store.getOrCreate({
        id: bundleId,
        type: 'curated',
        title: config.title,
        description: config.description,
        coverImage: config.coverImage,
        collectionHandle: config.collectionHandle,
        rules: config.rules && config.rules.length ? config.rules : rules,
        minSlots: config.minSlots
      });
    });
  }

  function initSlider(sliderEl) {
    var track = utils.qs('[data-mb-slider-track]', sliderEl);
    if (!track) return;
    var slides = utils.qsa('[data-mb-slider-track] > .mb-slider__slide', sliderEl);
    var dots = utils.qsa('[data-mb-slider-dot]', sliderEl);

    function slideWidth() {
      return slides[0] ? slides[0].getBoundingClientRect().width : track.getBoundingClientRect().width;
    }

    function goToIndex(index) {
      track.scrollTo({ left: slideWidth() * index, behavior: 'smooth' });
    }

    function setActiveDot(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('mb-slider__dot--active', i === index);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goToIndex(parseInt(dot.dataset.index, 10));
      });
    });

    // Drag-to-scroll for mouse users only; touch already gets native
    // momentum scrolling because the track uses overflow-x + scroll-snap
    // in CSS. Pointer Events fire for touch too though (they unify
    // mouse/touch/pen), and nothing here used to check pointerType - so
    // on mobile this manually set scrollLeft on every pointermove WHILE
    // the browser's own native touch-scroll was ALSO moving it from the
    // same finger drag, fighting each other and making the card feel
    // stuck/jumpy. Bail out immediately for any non-mouse pointer so
    // touch is untouched by this and only gets the native behavior.
    var isDown = false;
    var startX, scrollLeft;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      isDown = true;
      track.classList.add('mb-slider__track--dragging');
      startX = e.clientX;
      scrollLeft = track.scrollLeft;
    });
    window.addEventListener('pointerup', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      isDown = false;
      track.classList.remove('mb-slider__track--dragging');
    });
    track.addEventListener('pointermove', function (e) {
      if (!isDown || (e.pointerType && e.pointerType !== 'mouse')) return;
      track.scrollLeft = scrollLeft - (e.clientX - startX);
    });

    if (dots.length) {
      var updateDots = utils.debounce(function () {
        var index = Math.round(track.scrollLeft / slideWidth());
        setActiveDot(utils.clamp(index, 0, dots.length - 1));
      }, 100);
      track.addEventListener('scroll', updateDots);
    }
  }

  function bindCuratedCardActions() {
    document.addEventListener('click', function (e) {
      var viewBtn = e.target.closest('[data-mb-action="view-collection"]');
      if (viewBtn) {
        var bundleId = viewBtn.closest('[data-mb-curated-card]').dataset.bundleId;
        MB.collection.open(bundleId);
        return;
      }

      var shareBtn = e.target.closest('[data-mb-action="share"]');
      if (shareBtn) {
        handleShare(shareBtn);
      }
    });

    // The cover image is a div (role="button"/tabindex, not a real
    // <button>, since it just wraps the desktop/mobile <img> variants) -
    // clicks are already covered by the delegated listener above via
    // closest(), but keyboard activation needs its own handler.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var cover = e.target.closest('[data-mb-action="view-collection"][role="button"]');
      if (!cover) return;
      e.preventDefault();
      var bundleId = cover.closest('[data-mb-curated-card]').dataset.bundleId;
      MB.collection.open(bundleId);
    });
  }

  function handleShare(btn) {
    utils.shareLink(btn.dataset.shareUrl, btn.dataset.shareTitle);
  }

  // ---------------------------------------------------------------------
  // Create Your Own Pack
  // ---------------------------------------------------------------------

  function initCreateYourOwnPack(sectionEl, sectionId, rules) {
    var wrapper = utils.qs('[data-mb-create-pack]', sectionEl);
    if (!wrapper) return;

    var bundleId = wrapper.dataset.bundleId;
    var collectionHandle = wrapper.dataset.collectionHandle;

    var ctx = store.getOrCreate({
      id: bundleId,
      type: 'custom',
      title: wrapper.dataset.title || 'Create your own pack',
      collectionHandle: collectionHandle,
      rules: rules
    });
    store.setActive(bundleId);

    var gridEl = utils.qs('[data-mb-grid]', wrapper);
    var controller = MB.collection.createGridController(gridEl, {
      bundleId: bundleId,
      collectionHandle: collectionHandle,
      mode: 'add',
      excludeSelected: false
    });

    var toolbarEl = utils.qs('[data-mb-search-toolbar]', wrapper);
    if (toolbarEl) {
      bindSearchInput(toolbarEl, controller, bundleId);
      bindSortSelect(toolbarEl, controller);
    }

    var filterPanel = utils.qs('[data-mb-filter-panel]', wrapper);
    if (filterPanel) bindFilterPanel(filterPanel, controller, wrapper);

    controller.load();
  }

  function bindSearchInput(toolbarEl, controller, bundleId) {
    var input = utils.qs('[data-mb-search]', toolbarEl);
    if (!input) return;
    var debounced = utils.debounce(function () { controller.setQuery(input.value); }, 250);
    input.addEventListener('input', debounced);

    MB.collection.bindSearchSuggestions(
      input,
      function () { return controller.getProducts(); },
      function (product) { MB.product.openQuickView(product.handle, bundleId); }
    );
  }

  function bindSortSelect(toolbarEl, controller) {
    var select = utils.qs('[data-mb-sort]', toolbarEl);
    if (!select) return;
    select.addEventListener('change', function () { controller.setSort(select.value); });
  }

  function bindFilterPanel(panelEl, controller, wrapper) {
    var pendingFilters = {};

    document.addEventListener('multibuy:grid:loaded', function (e) {
      if (e.detail.container !== utils.qs('[data-mb-grid]', wrapper)) return;
      populateFacets(panelEl, e.detail.facets);
    });

    // Two toggle buttons can exist at once (desktop vs. mobile variants,
    // shown/hidden purely via CSS) - bind all of them, not just the first.
    utils.qsa('[data-mb-filter-toggle]', wrapper).forEach(function (btn) {
      btn.addEventListener('click', function () {
        panelEl.classList.toggle('mb-filter-panel--open');
      });
    });

    panelEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-mb-filter-close]')) {
        panelEl.classList.remove('mb-filter-panel--open');
      }
      if (e.target.closest('[data-mb-filter-apply]')) {
        applyPendingFilters();
        panelEl.classList.remove('mb-filter-panel--open');
      }
      if (e.target.closest('[data-mb-filter-reset]')) {
        utils.qsa('input[type="checkbox"]', panelEl).forEach(function (cb) { cb.checked = false; });
        pendingFilters = {};
        var minInput = utils.qs('[data-mb-filter-min]', panelEl);
        var maxInput = utils.qs('[data-mb-filter-max]', panelEl);
        if (minInput) minInput.value = '';
        if (maxInput) maxInput.value = '';
        utils.qsa('input[type="range"]', panelEl).forEach(function (range) {
          range.value = range.classList.contains('mb-range--min') ? range.min : range.max;
        });
        controller.setFilters({});
      }
    });

    function applyPendingFilters() {
      var minInput = utils.qs('[data-mb-filter-min]', panelEl);
      var maxInput = utils.qs('[data-mb-filter-max]', panelEl);

      var filters = {
        minPrice: minInput && minInput.value ? Math.round(parseFloat(minInput.value) * 100) : undefined,
        maxPrice: maxInput && maxInput.value ? Math.round(parseFloat(maxInput.value) * 100) : undefined
      };
      MB.filter.facetKeys.forEach(function (key) {
        filters[key] = utils.qsa('[data-mb-filter-' + key + ']:checked', panelEl).map(function (cb) { return cb.value; });
      });

      controller.setFilters(filters);
    }
  }

  function populateFacets(panelEl, facets) {
    MB.filter.facetKeys.forEach(function (key) {
      var group = utils.qs('[data-mb-filter-group="' + key + '"]', panelEl);
      var values = facets[key] || [];
      renderCheckboxGroup(utils.qs('[data-mb-facet-' + key + ']', panelEl), values, 'mb-filter-' + key);
      // Only show a filter that actually has values for this collection.
      if (group) group.hidden = values.length === 0;
    });

    var minInput = utils.qs('[data-mb-filter-min]', panelEl);
    var maxInput = utils.qs('[data-mb-filter-max]', panelEl);
    if (minInput && !minInput.value) minInput.value = (facets.minPrice / 100).toFixed(0);
    if (maxInput && !maxInput.value) maxInput.value = (facets.maxPrice / 100).toFixed(0);

    initDualRangeSlider(panelEl, facets.minPrice / 100, facets.maxPrice / 100);
  }

  function renderCheckboxGroup(container, values, attr) {
    if (!container) return;
    container.innerHTML = values
      .map(function (v) {
        return (
          '<label class="mb-checkbox"><input type="checkbox" data-' + attr + ' value="' + utils.escapeHtml(v) + '"><span>' + utils.escapeHtml(v) + '</span></label>'
        );
      })
      .join('');
  }

  function initDualRangeSlider(panelEl, min, max) {
    var container = utils.qs('[data-mb-price-slider]', panelEl);
    var minInput = utils.qs('[data-mb-filter-min]', panelEl);
    var maxInput = utils.qs('[data-mb-filter-max]', panelEl);
    if (!container || !minInput || !maxInput) return;

    container.innerHTML =
      '<input type="range" class="mb-range mb-range--min" min="' + min + '" max="' + max + '" value="' + minInput.value + '">' +
      '<input type="range" class="mb-range mb-range--max" min="' + min + '" max="' + max + '" value="' + maxInput.value + '">';

    var minRange = utils.qs('.mb-range--min', container);
    var maxRange = utils.qs('.mb-range--max', container);

    minRange.addEventListener('input', function () {
      if (parseFloat(minRange.value) > parseFloat(maxRange.value)) minRange.value = maxRange.value;
      minInput.value = minRange.value;
    });
    maxRange.addEventListener('input', function () {
      if (parseFloat(maxRange.value) < parseFloat(minRange.value)) maxRange.value = minRange.value;
      maxInput.value = maxRange.value;
    });
  }

  // Exposed so other grids that aren't Create Your Own Pack (e.g. the
  // Replace Selection page in collection.js) can wire up the exact same
  // search/sort/filter-panel behavior instead of re-implementing it.
  MB.filterUI = {
    bindSearchInput: bindSearchInput,
    bindSortSelect: bindSortSelect,
    bindFilterPanel: bindFilterPanel
  };

  // ---------------------------------------------------------------------
  // Toasts
  // ---------------------------------------------------------------------

  function bindToasts() {
    var toastEl = utils.qs('[data-mb-toast]');
    document.addEventListener('multibuy:toast', function (e) {
      if (!toastEl) return;
      toastEl.textContent = e.detail.message;
      toastEl.classList.toggle('mb-toast--size', e.detail.kind === 'size');
      toastEl.classList.toggle('mb-toast--error', !!e.detail.error);
      toastEl.classList.add('mb-toast--visible');
      clearTimeout(toastEl._hideTimer);
      toastEl._hideTimer = setTimeout(function () {
        toastEl.classList.remove('mb-toast--visible');
      }, 2600);
    });
  }

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------

  function boot() {
    var settingsEl = document.getElementById('mb-settings');
    if (settingsEl) {
      try {
        MB.settings = JSON.parse(settingsEl.textContent);
      } catch (err) {
        MB.settings = {};
      }
    }

    MB.bundleUI.bindFooterEvents();
    MB.collection.bindGridEvents();
    MB.collection.bindSheetEvents();
    MB.product.bindQuickViewEvents();
    bindCuratedCardActions();
    bindToasts();

    utils.qsa('[data-mb-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var sectionEl = utils.qs('[data-mb-section]', e.target);
    if (sectionEl) initSection(sectionEl);
  });
})(window, document);
